import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { axialToWorld } from '@/game/hexmap';
import config from '@/game/hexmap/config';
import { colorIndexFor, PLAYER_COLORS } from '@/game/view/palette';
import { citySprawl, visibleSprawl } from '@/game/view/sprawl';
import { useSimFrame } from './app/simFrame';
import { placeMatrix, sprawlLook, useModelBatcher } from './ModelBatcher';

const THICKNESS = config.hexMap.tile.thickness;
const scratchMatrix = new THREE.Matrix4();

/**
 * Cosmetic city sprawl (see `sprawl.ts`) through the shared `ModelBatcher`:
 * own cities' always, rivals' only while the tile and its city are in sight
 * (`visibleSprawl`). Never takes a click (the batcher's meshes have no
 * handlers), so tiles and buildings under it still get selections and orders.
 */
export function CitySprawlLayer() {
  const batcher = useModelBatcher();
  const frame = useSimFrame();
  const { playerOrder } = frame;
  /** Entity ids this layer has in the batcher. */
  const placed = useRef(new Set<string>());

  // Rivals' sprawl by sight alone: it's part of the tile, like the terrain.
  const tiles = useMemo(() => {
    const { view, ctx, playerId, fog } = frame;
    return [...visibleSprawl(citySprawl(view, ctx), view, playerId, fog.sight).values()];
  }, [frame]);

  useLayoutEffect(() => {
    const keep = new Set<string>();
    for (const tile of tiles) {
      const id = `s:${tile.key}`;
      keep.add(id);
      const world = axialToWorld(tile.pos, config.hexMap.tile.radius);
      batcher.set(
        id,
        sprawlLook(tile.level, tile.variant),
        placeMatrix(scratchMatrix, world.x, THICKNESS, world.z),
        PLAYER_COLORS[colorIndexFor(tile.owner, playerOrder)] ?? PLAYER_COLORS[0]
      );
      placed.current.add(id);
    }
    for (const id of placed.current) {
      if (keep.has(id)) continue;
      batcher.remove(id);
      placed.current.delete(id);
    }
  }, [tiles, batcher, playerOrder]);
  useEffect(
    () => () => {
      for (const id of placed.current) batcher.remove(id);
      placed.current.clear();
    },
    [batcher]
  );

  return null;
}
